// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ReactNode, RefObject } from 'react';

import { TREATMENTS, type ResourceKey, type Sample, type SurveyType, type SurveyTypePatch, type Treatment } from '../../../api/types';
import { newSampleHref } from '../../../app/links';
import { useTextDraft, fieldKeys } from '../../../app/useTextDraft';
import { calcMethod, calcTonnes, capitalize, hasServerMass, takesBuildup, type Buildup } from '../../../bygning/buildups';
import {
  eakChoices,
  isHazardousEak,
  keyForRole,
  typeValue,
  yesNo,
  type FieldGroupSpec,
  type FieldSpec,
} from '../../../bygning/fields';
import { samplesOfType } from '../../../bygning/parts';
import { classifiedTag, eakTag, massView, quantityIsCount, quantityTag, serverTag } from '../../../bygning/provenance';
import { NON_CLEARABLE } from '../../../kortlaegning/cellDraft';
import type { ResourceIndex } from '../../../kortlaegning/resources';
import { STAGE_LABEL, TREATMENT_LABEL, formatNumber, type Tone } from '../../../kortlaegning/vocab';
import { Pill } from '../../Pill';
import { Select } from '../../Select';
import { TextInput } from '../../TextInput';
import { TextLink } from '../../TextLink';
import { ResourceCell } from '../../kortlaegning/ResourceCell';
import { BuildupPicker } from './BuildupPicker';
import { FieldGroup } from './FieldGroup';
import { FieldRow } from './FieldRow';
import styles from './Inspector.module.css';
import { NumberWithUnit } from './NumberWithUnit';
import { RoomChips } from './RoomChips';
import { TagToggles } from './TagToggles';
import { YesNo } from './YesNo';

export interface TypeFieldsProps {
  type: SurveyType;
  types: readonly SurveyType[];
  groups: FieldGroupSpec[];
  /** Show the BEK 496 references (Alle felter). */
  showRefs: boolean;
  index: ResourceIndex;
  catalogue: readonly ResourceKey[];
  samples: readonly Sample[];
  buildup: Buildup | null;
  buildupMixed: boolean;
  /** The build-ups to pick from (`GET /buildups`, or the embedded copy on an older server). */
  library: readonly Buildup[];
  /** The old `Opbygning` column's build-up, offered on a server that keeps `buildup_id` while the type has none. */
  legacyBuildup: Buildup | null;
  room: number | null;
  canEdit: boolean;
  busy: boolean;
  home: RefObject<HTMLElement | null>;
  onRoom: (room: number | null) => void;
  onSample: (sampleId: number) => void;
  onPatchType: (patch: SurveyTypePatch) => void;
  onCommitKey: (key: ResourceKey, value: string | null) => Promise<void>;
  onInvalid: (label: string) => void;
  /** null clears the build-up (a server that keeps `buildup_id`). */
  onPickBuildup: (b: Buildup | null) => void;
  onSplitLayer: (index: number) => void;
}

/** The Opbygning row's tag from the server's provenance: `foreslået` when measure suggested it. */
function serverBuildupTag(type: SurveyType) {
  if (!type.buildup_id) return null;
  return serverTag(type.provenance?.buildup_id);
}

function sampleTone(s: Sample): Tone {
  if (s.stage === 'svar') return s.result === 'forurenet' ? 'crit' : 'good';
  return s.stage === 'sendt' ? 'warn' : 'wait';
}

function TypeNote({ type, canEdit, onPatchType, home }: Pick<TypeFieldsProps, 'type' | 'canEdit' | 'onPatchType' | 'home'>) {
  const draft = useTextDraft(type.note, (note) => onPatchType({ note }));
  const keys = fieldKeys(draft, home);
  return <TextInput variant="filled" size="lg" block aria-label="Note" readOnly={!canEdit} {...draft.props} onKeyDown={keys} />;
}

/**
 * The inspector's field groups for one field view (`bygning/fields`: the
 * template's keys in its order, a heading per category run, the report's
 * rows): each row's control is the value, with its provenance tag
 * (`bygning/provenance`).
 * Type-level fields (Mængde, EAK, Tons, Behandling, Note) write the type;
 * resource keys write through `onCommitKey` (carrier or every part, by scope).
 */
export function TypeFields(props: TypeFieldsProps) {
  const { type, groups, showRefs, index, catalogue, samples, buildup, canEdit, busy, home } = props;
  const editable = (k: ResourceKey | null) => canEdit && !!k && k.editable;
  const valueOf = (k: ResourceKey) => typeValue(type, k, index);
  const noParts = type.parts.length === 0;
  const linked = samplesOfType(type, samples);
  const forurenetAnswer = linked.some((s) => s.stage === 'svar' && s.result === 'forurenet');
  const hazardNeed = forurenetAnswer && !isHazardousEak(type.eak_code);
  const hasRole = (role: FieldSpec['role']) => groups.some((g) => g.fields.some((f) => f.role === role));
  const noteKey = keyForRole('forureningNote', catalogue);

  const mixedSub = (k: ResourceKey) => {
    const v = valueOf(k);
    return v.mixed ? `Forskellige værdier på delene — et valg her gælder alle ${type.parts.length}` : undefined;
  };

  /** A resource key's editor by data type. */
  function keyControl(k: ResourceKey): ReactNode {
    const { value } = valueOf(k);
    if (noParts) return <span className={`${styles.text} ${styles.textMissing}`}>Ingen dele at gemme på</span>;
    if (k.data_type === 'boolean') {
      return (
        <YesNo
          label={k.label}
          value={value}
          disabled={!editable(k)}
          clearable={!NON_CLEARABLE.has(k.id)}
          onChange={(v) => props.onCommitKey(k, v)}
        />
      );
    }
    if (k.data_type === 'multiselect') {
      return <TagToggles label={k.label} options={k.options} value={value} disabled={!editable(k)} onChange={(v) => props.onCommitKey(k, v)} />;
    }
    return (
      <ResourceCell
        key={`${type.id}:${k.id}`}
        resourceKey={k}
        value={value}
        editing={editable(k)}
        variant="field"
        onCommit={(v) => props.onCommitKey(k, v)}
        onInvalid={props.onInvalid}
        home={home}
      />
    );
  }

  function forureningNote(): ReactNode {
    if (!noteKey) return null;
    return keyControl(noteKey);
  }

  function row(f: FieldSpec): ReactNode {
    const ref = showRefs ? f.ref : null;
    const k = f.key;
    switch (f.role) {
      case 'materiale': {
        if (!k) return null;
        return (
          <FieldRow key={f.id} label={f.label} legalRef={ref} control={keyControl(k)} tag={classifiedTag(type, valueOf(k).value, k.id)} sub={mixedSub(k)} />
        );
      }
      case 'forurenet': {
        if (!k) return null;
        const on = yesNo(valueOf(k).value) === true;
        return (
          <FieldRow
            key={f.id}
            label={f.label}
            legalRef={ref}
            control={keyControl(k)}
            sub={mixedSub(k)}
            extra={on && noteKey && !noParts ? forureningNote() : undefined}
          />
        );
      }
      case 'forureningNote': {
        // Shown under Potentielt forurenet when that row is in the view.
        if (!k || hasRole('forurenet')) return null;
        return <FieldRow key={f.id} label={f.label} legalRef={ref} control={keyControl(k)} sub={mixedSub(k)} />;
      }
      case 'quantity': {
        const count = quantityIsCount(type);
        return (
          <FieldRow
            key={f.id}
            label={f.label}
            legalRef={ref}
            text={count ? `${formatNumber(type.quantity)} ${type.unit}` : undefined}
            control={
              count ? undefined : (
                <NumberWithUnit
                  key={`q:${type.id}`}
                  label={`Mængde i ${type.unit}`}
                  value={type.quantity}
                  unit={type.unit}
                  readOnly={!canEdit || noParts}
                  home={home}
                  onInvalid={props.onInvalid}
                  onCommit={(q) => q !== null && props.onPatchType({ quantity: q })}
                />
              )
            }
            tag={quantityTag(type)}
            sub={noParts ? 'Tilføj en del for at angive mængden' : type.parts.length > 1 && !count ? 'Fordeles på delene' : undefined}
          />
        );
      }
      case 'rooms':
        return <FieldRow key={f.id} label={f.label} legalRef={ref} control={<RoomChips type={type} focused={props.room} onFocus={props.onRoom} />} />;
      case 'buildup': {
        if (!takesBuildup(type)) return null;
        const server = hasServerMass(type);
        const legacy = props.legacyBuildup;
        return (
          <FieldRow
            key={f.id}
            label={f.label}
            legalRef={ref}
            control={
              <BuildupPicker
                type={type}
                types={props.types}
                library={props.library}
                current={buildup}
                mixed={props.buildupMixed}
                clearable={server}
                canEdit={canEdit}
                busy={busy}
                onPick={props.onPickBuildup}
                onSplit={props.onSplitLayer}
              />
            }
            tag={server ? serverBuildupTag(type) : null}
            after={
              legacy && canEdit ? (
                <TextLink size="sm" nowrap disabled={busy} onClick={() => props.onPickBuildup(legacy)}>
                  Brug {legacy.name}
                </TextLink>
              ) : undefined
            }
            sub={legacy ? `Kolonnen Opbygning siger ${legacy.name} — vælg den her, så regner serveren tonsene` : undefined}
          />
        );
      }
      case 'eak': {
        const e = eakTag(type, buildup, hazardNeed);
        return (
          <FieldRow
            key={f.id}
            label={f.label}
            legalRef={ref}
            critical={hazardNeed}
            control={
              <Select
                variant="filled"
                size="lg"
                block
                aria-label="EAK-kode"
                value={type.eak_code}
                disabled={!canEdit}
                onChange={(ev) => props.onPatchType({ eak_code: ev.target.value })}
              >
                <option value="">Ingen EAK</option>
                {eakChoices(type.eak_code).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </Select>
            }
            tag={e.conflict ? { kind: 'entered', label: 'passer ikke med opbygningen', warn: true } : e.tag}
            after={
              e.suggestion && canEdit ? (
                <TextLink size="sm" nowrap onClick={() => props.onPatchType({ eak_code: e.suggestion as string })}>
                  Brug {e.suggestion}
                </TextLink>
              ) : undefined
            }
            sub={hazardNeed ? 'Prøvesvaret er forurenet · overvej 17.06.05* eller 17.09.02*' : type.eak_name || undefined}
          />
        );
      }
      case 'tons': {
        const server = hasServerMass(type);
        const clientCalc = server ? null : calcTonnes(type, buildup);
        const view = massView(type, buildup, clientCalc, !server && buildup ? calcMethod(type, buildup) : null);
        const use = view.useCalc;
        return (
          <FieldRow
            key={f.id}
            label={f.label}
            legalRef={ref}
            control={
              <NumberWithUnit
                key={`t:${type.id}`}
                label="Vægt i tons"
                value={view.value}
                unit="t"
                clearable
                placeholder="–"
                readOnly={!canEdit}
                home={home}
                onInvalid={props.onInvalid}
                onCommit={(t) => {
                  // Retyping the calculated figure is not an override.
                  if (server && view.source === 'calculated' && (t === null || t === view.value)) return;
                  props.onPatchType({ mass_t: t });
                }}
              />
            }
            tag={view.tag}
            after={
              use && canEdit ? (
                <TextLink
                  size="sm"
                  nowrap
                  title={use.kind === 'clear' ? 'Fjern den indtastede vægt — den beregnede gælder' : undefined}
                  onClick={() => props.onPatchType({ mass_t: use.kind === 'clear' ? null : use.tonnes })}
                >
                  {use.kind === 'clear' ? `Brug beregnet (${formatNumber(use.tonnes)} t)` : `Brug ${formatNumber(use.tonnes)} t`}
                </TextLink>
              ) : undefined
            }
            sub={view.sub ?? undefined}
            extra={
              view.warnings.length > 0 ? (
                <ul className={styles.warnList} aria-label="Advarsler om vægten">
                  {view.warnings.map((w) => (
                    <li key={w}>{capitalize(w)}</li>
                  ))}
                </ul>
              ) : undefined
            }
          />
        );
      }
      case 'treatment':
        return (
          <FieldRow
            key={f.id}
            label={f.label}
            legalRef={ref}
            control={
              <Select
                variant="filled"
                size="lg"
                block
                aria-label="Behandling"
                value={type.treatment}
                disabled={!canEdit}
                onChange={(ev) => props.onPatchType({ treatment: ev.target.value as Treatment })}
              >
                {TREATMENTS.map((t) => (
                  <option key={t} value={t}>
                    {TREATMENT_LABEL[t]}
                  </option>
                ))}
              </Select>
            }
          />
        );
      case 'stoffer': {
        if (linked.length > 0) {
          return linked.map((s) => (
            <FieldRow
              key={`${f.id}:${s.id}`}
              label={f.label}
              legalRef={ref}
              text={`${s.title} (${s.code})`}
              control={<Pill tone={sampleTone(s)}>{STAGE_LABEL[s.stage]}</Pill>}
              after={
                <TextLink size="sm" nowrap onClick={() => props.onSample(s.id)}>
                  Se prøven
                </TextLink>
              }
            />
          ));
        }
        const forKey = keyForRole('forurenet', catalogue);
        const suspected = forKey ? yesNo(valueOf(forKey).value) === true : false;
        return (
          <FieldRow
            key={f.id}
            label={f.label}
            legalRef={ref}
            text={suspected ? 'Mistanke uden prøve' : 'Ingen prøver'}
            missing={suspected}
            after={
              canEdit ? (
                <TextLink size="sm" nowrap to={newSampleHref(type.id)}>
                  Opret prøve
                </TextLink>
              ) : undefined
            }
          />
        );
      }
      case 'note':
        return <FieldRow key={f.id} label={f.label} legalRef={ref} control={<TypeNote key={type.id} type={type} canEdit={canEdit} onPatchType={props.onPatchType} home={home} />} />;
      default: {
        if (!k) return null;
        return <FieldRow key={f.id} label={f.label} legalRef={ref} control={keyControl(k)} sub={mixedSub(k)} />;
      }
    }
  }

  return (
    <>
      {groups.map((g) => (
        <FieldGroup key={g.id} title={g.title}>
          {g.fields.map(row)}
        </FieldGroup>
      ))}
    </>
  );
}
